import { useState } from "react";
import moment from "moment-timezone";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { DatePicker } from "@/components/ui/typeable_date_picker";
import { TimePicker } from "@/components/ui/time_picker";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import {
  useSendAdhocMessage,
  SendAdhocDestinationMessagesForValidation,
} from "@/lib/api/destination";
import { CommonDestinationTimezone, getUniqueStrings } from "@/lib/utils";

interface AdhocMessageModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedRowData: {
    content_id: string;
    destination_id: string;
    commodity: string;
    filteredReceivers: Array<{
      receiver_id?: string;
      internal_user_id?: string;
      receiver_name: string;
      user_type: "freight" | "internal";
    }>;
  };
}

export function AdhocMessageModal({ isOpen, onClose, selectedRowData }: AdhocMessageModalProps) {
  const [message, setMessage] = useState("");
  const [scheduledDate, setScheduledDate] = useState<Date | undefined>(undefined);
  const [scheduledTime, setScheduledTime] = useState("08:00:00");
  const [isTest, setIsTest] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const sendAdhocMessage = useSendAdhocMessage();

  const handleSubmit = async () => {
    try {
      if (isSubmitting) return;
      setIsSubmitting(true);

      // Validate scheduled date is in the future if provided
      if (scheduledDate && moment(scheduledDate).isSameOrBefore(moment().endOf("day"))) {
        toast({
          title: "Invalid date",
          description: "Date must be in the future",
        });
        return;
      }

      const producer_freight_ids = getUniqueStrings(selectedRowData.filteredReceivers
        .filter(r => r.receiver_id)
        .map(r => r.receiver_id!));

      const internal_user_ids = getUniqueStrings(selectedRowData.filteredReceivers
        .filter(r => r.internal_user_id)
        .map(r => r.internal_user_id!));

      const adhocData = {
        is_test: isTest,
        commodity: selectedRowData.commodity,
        destination_id: selectedRowData.destination_id,
        producer_freight_ids,
        internal_user_ids,
        send_at: scheduledDate ? moment(scheduledDate).format("YYYY-MM-DD") : undefined,
        send_at_time: scheduledTime,
        message: message.trim(),
      };

      // Validate using safe parse
      const validation = SendAdhocDestinationMessagesForValidation.safeParse(adhocData);

      if (!validation.success) {
        toast({
          title: "Validation Error",
          description: "Please check all fields are filled correctly",
        });
        setIsSubmitting(false);
        return;
      }

      const result = await sendAdhocMessage(validation.data);

      if (result.success) {
        toast({
          title: "Message sent",
          description: scheduledDate
            ? `Scheduled for ${moment(scheduledDate).format("YYYY-MM-DD")}`
            : "Sending now",
        });
        handleClose();
      } else {
        toast({
          title: "Failed to send message",
          description: "",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "An unexpected error occurred",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setMessage("");
    setScheduledDate(undefined);
    setScheduledTime("08:00:00");
    setIsTest(false);
    onClose();
  };

  const producerFreightReceivers = selectedRowData.filteredReceivers.filter(r => r.user_type === "freight");
  const internalUserReceivers = selectedRowData.filteredReceivers.filter(r => r.user_type === "internal");

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg md:max-w-2xl lg:max-w-4xl">
        <DialogHeader>
          <DialogTitle>Send Adhoc Message</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-4">
          {/* Recipients Display */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium mb-2 block">Producer/Freight Recipients</Label>
              <div className="border rounded p-3 min-h-[100px] max-h-[150px] overflow-y-auto">
                {producerFreightReceivers.length > 0 ? (
                  <ul className="space-y-1">
                    {producerFreightReceivers.map((receiver, index) => (
                      <li key={index} className="text-sm">
                        {receiver.receiver_name}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-muted-foreground">No producer/freight recipients</p>
                )}
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium mb-2 block">Internal User Recipients</Label>
              <div className="border rounded p-3 min-h-[100px] max-h-[150px] overflow-y-auto">
                {internalUserReceivers.length > 0 ? (
                  <ul className="space-y-1">
                    {internalUserReceivers.map((receiver, index) => (
                      <li key={index} className="text-sm">
                        {receiver.receiver_name}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-muted-foreground">No internal user recipients</p>
                )}
              </div>
            </div>
          </div>

          {/* Message Input */}
          <div>
            <Label htmlFor="message">Message</Label>
            <Textarea
              id="message"
              placeholder="Enter your message here..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="mt-1"
              rows={4}
            />
          </div>

          {/* Scheduling Options */}
          <div className="flex gap-4">
            <div className="grid gap-2">
              <Label>Schedule Date (Optional)</Label>
              <DatePicker
                value={scheduledDate}
                onChange={(date) => setScheduledDate(date as Date)}
                placeholder="Select date"
                className="w-full"
                minDate={new Date()}
              />
            </div>

            <div className="grid gap-2">
              <Label>Schedule Time</Label>
              <TimePicker
                value={scheduledTime}
                onChange={(time) => setScheduledTime(time || "08:00:00")}
              />
            </div>

            <div className="flex gap-2">
              <Checkbox
                id="test"
                checked={isTest}
                onCheckedChange={(checked: boolean) => setIsTest(checked)}
              />
              <Label htmlFor="test">Test Message</Label>
            </div>
          </div>

          {/* Summary Info */}
          <div className="bg-muted p-3 rounded text-sm">
            <p><strong>Commodity:</strong> {selectedRowData.commodity}</p>
            <p><strong>Total Recipients:</strong> {selectedRowData.filteredReceivers.length}</p>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || (!message || message.trim().length < 5)}
          >
            {isSubmitting ? "Sending..." : scheduledDate ? "Schedule" : "Send Now"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
